// =============================================================================
// MobileInventoryIssueSheet.tsx
// Sub-sheet that slides up over the inventory detail sheet. Type-to-search
// staff field, qty stepper clamped to current available, optional notes,
// then a single "Issue" submit. Higher z-index than the detail sheet so it
// stacks cleanly on top.
// =============================================================================

import React, { useMemo, useState } from 'react';

import { inventoryIssue } from '../../lib/api';

import { StaffMember } from '../../lib/types';

import { S } from './mobileStyles';

// ── TYPES ────────────────────────────────────────────────────────────

interface Props {
  item: any;
  staffDirectory: StaffMember[];
  onClose: () => void;
  onDone: () => void;
  showToast: (msg: string, type?: 'success' | 'error') => void;
}

// ── CONSTANTS ────────────────────────────────────────────────────────

// Higher than the detail sheet (300) so the detail stays visible beneath.
const SUB_SHEET_Z = 400;

// ── MAIN COMPONENT ───────────────────────────────────────────────────

export default function MobileInventoryIssueSheet({
  item, staffDirectory, onClose, onDone, showToast,
}: Props) {
  const [query, setQuery] = useState('');
  const [selectedStaff, setSelectedStaff] = useState<StaffMember | null>(null);
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [closing, setClosing] = useState(false);

  const max = item.available ?? 0;

  // Type-to-search: no visible list until user types. Show up to 8 matches.
  const matches = useMemo(() => {
    if (!query.trim() || selectedStaff) return [];
    const q = query.trim().toLowerCase();
    return staffDirectory
      .filter(s => s.active !== false)
      .filter(s => (s.name || '').toLowerCase().includes(q) || (s.short_name || '').toLowerCase().includes(q))
      .slice(0, 8);
  }, [query, staffDirectory, selectedStaff]);

  const pick = (s: StaffMember) => {
    setSelectedStaff(s);
    setQuery(s.name);
  };

  const clearStaff = () => {
    setSelectedStaff(null);
    setQuery('');
  };

  const handleClose = () => {
    if (closing) return;
    setClosing(true);
    setTimeout(onClose, 350);
  };

  const canSubmit = !!selectedStaff && qty >= 1 && qty <= max && !busy;

  const submit = async () => {
    if (!canSubmit || !selectedStaff) return;
    setBusy(true);
    const noteLine = notes.trim()
      ? `Issued to: ${selectedStaff.name} — ${notes.trim()}`
      : `Issued to: ${selectedStaff.name}`;
    const result = await inventoryIssue(item.id, qty, undefined, noteLine);
    if (result.ok) {
      showToast(`Issued ${qty} to ${selectedStaff.name}`, 'success');
      setBusy(false);
      setClosing(true);
      setTimeout(() => { onDone(); }, 350);
    } else {
      setBusy(false);
      showToast(result.message || result.error || 'Issue failed', 'error');
    }
  };

  return (
    <div
      style={{ ...S.sheet, zIndex: SUB_SHEET_Z }}
      className={closing ? 'm-sheet-closing' : 'm-sheet-backdrop'}
      onClick={handleClose}
    >
      <div
        style={{ ...S.sheetContent, maxHeight: '85vh' }}
        className="m-sheet-content"
        onClick={e => e.stopPropagation()}
      >
        <div style={S.handle as any} />

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '4px 20px 12px', gap: 12, borderBottom: '0.5px solid var(--border)',
        }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text)', flex: 1, wordBreak: 'break-word' }}>
            Issue {item.name}
          </div>
          <button
            onClick={handleClose}
            aria-label="Close"
            style={{
              width: 44, height: 44, minWidth: 44, minHeight: 44,
              background: 'var(--surface2)', border: 'none', borderRadius: 12,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0,
            }}
          >
            <svg viewBox="0 0 20 20" style={{ width: 16, height: 16, fill: 'none', stroke: 'var(--text2)', strokeWidth: 2, strokeLinecap: 'round' }}>
              <path d="M4 4l12 12M16 4L4 16" />
            </svg>
          </button>
        </div>

        {/* Available hint */}
        <div style={{ padding: '10px 20px 4px', fontSize: 'var(--m-text-label)', color: 'var(--text2)' }}>
          {max} in stock
        </div>

        {/* Form */}
        <div style={{ padding: '8px 20px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Staff field */}
          <div>
            <label style={S.label}>Staff member</label>
            <div style={{ position: 'relative' }}>
              <input
                value={query}
                onChange={e => {
                  setQuery(e.target.value);
                  if (selectedStaff && e.target.value !== selectedStaff.name) setSelectedStaff(null);
                }}
                placeholder="Type staff name…"
                style={{ ...S.input, paddingRight: selectedStaff ? 40 : 12 }}
              />
              {selectedStaff && (
                <button
                  onClick={clearStaff}
                  aria-label="Clear"
                  style={{
                    position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                    width: 32, height: 32, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                  }}
                >
                  <svg viewBox="0 0 20 20" style={{ width: 12, height: 12, fill: 'none', stroke: 'var(--text2)', strokeWidth: 2 }}>
                    <path d="M4 4l12 12M16 4L4 16" />
                  </svg>
                </button>
              )}
            </div>

            {matches.length > 0 && (
              <div style={{
                marginTop: 6, background: 'var(--surface2)', borderRadius: 10,
                border: '1px solid var(--border)', maxHeight: 260, overflowY: 'auto',
              }}>
                {matches.map(s => (
                  <button
                    key={s.id}
                    onClick={() => pick(s)}
                    style={{
                      display: 'block', width: '100%', minHeight: 44, textAlign: 'left',
                      padding: '10px 12px', fontSize: 'var(--m-text-meta)', color: 'var(--text)',
                      background: 'none', border: 'none', borderBottom: '0.5px solid var(--border)',
                      cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Quantity stepper */}
          <div>
            <label style={S.label}>Quantity</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button
                onClick={() => setQty(Math.max(1, qty - 1))}
                style={{
                  width: 44, height: 44, background: 'var(--surface2)', border: '1px solid var(--border)',
                  borderRadius: 10, fontSize: 20, color: 'var(--text)', cursor: 'pointer', fontFamily: 'inherit',
                }}
              >−</button>
              <input
                type="number"
                inputMode="numeric"
                value={qty}
                min={1}
                max={max}
                onChange={e => {
                  const n = parseInt(e.target.value, 10);
                  if (Number.isNaN(n)) setQty(1);
                  else setQty(Math.max(1, Math.min(max, n)));
                }}
                style={{ ...S.input, textAlign: 'center', flex: 1, fontWeight: 600, fontSize: 18 }}
              />
              <button
                onClick={() => setQty(Math.min(max, qty + 1))}
                style={{
                  width: 44, height: 44, background: 'var(--surface2)', border: '1px solid var(--border)',
                  borderRadius: 10, fontSize: 20, color: 'var(--text)', cursor: 'pointer', fontFamily: 'inherit',
                }}
              >+</button>
            </div>
            {qty > max && (
              <div style={{ fontSize: 'var(--m-text-label)', color: 'var(--red)', marginTop: 4 }}>
                Only {max} in stock
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label style={S.label}>Notes (optional)</label>
            <input
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Optional notes…"
              style={S.input}
            />
          </div>

          {/* Submit */}
          <button
            onClick={submit}
            disabled={!canSubmit}
            style={{
              ...S.greenBtn,
              marginTop: 4,
              opacity: canSubmit ? 1 : 0.5,
              cursor: canSubmit ? 'pointer' : 'not-allowed',
            }}
          >
            {busy ? 'Issuing…' : 'Issue'}
          </button>
        </div>
      </div>
    </div>
  );
}
